import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ChevronDown, ChevronUp, Inbox, Loader2, Mail, MessageSquare, Paperclip, RefreshCw, Reply, Send, User } from "lucide-react";
import { toast } from "sonner";

/**
 * 🆕 Timeline "Comunicazioni" per un preventivo:
 * - Email in INGRESSO (Inbox collegate via numero PR, email cliente o email broker)
 * - Email in USCITA (invii a partner via `quote_send_logs`)
 * Ordinate dal più recente. Toggle espandi/collassa per non ingombrare la modifica.
 */
export function QuoteCommunications({ quoteId }) {
  const [expanded, setExpanded] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openBody, setOpenBody] = useState(null); // event.id per il quale mostrare il corpo completo
  // 🆕 Reply dialog per rispondere alle email in ingresso direttamente dalla timeline
  const [replyDialog, setReplyDialog] = useState(null); // { emailId, to, subject, body }
  const [replySending, setReplySending] = useState(false);

  const load = useCallback(async () => {
    if (!quoteId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/quotes/${quoteId}/communications`);
      setEvents(Array.isArray(data) ? data : []);
    } catch {
      // Silenzioso: se non ci sono comunicazioni non è un errore
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    if (expanded && quoteId) load();
  }, [expanded, quoteId, load]);

  const runBackfill = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/inbox/link-existing");
      toast.success(`Backfill completato: ${data.linked}/${data.scanned} email collegate`);
      await load();
    } catch (e) {
      toast.error("Errore durante il backfill");
    } finally {
      setLoading(false);
    }
  };

  const count = events.length;
  const inCount = events.filter((e) => e.direction === "in").length;
  const outCount = events.filter((e) => e.direction === "out").length;

  const sourceLabel = (s) => ({
    quote_number: "🔗 Numero PR",
    client_email: "👤 Email cliente",
    broker_email: "🏢 Email broker",
  }[s] || "");

  // 🆕 Apre il dialog di risposta pre-compilando to/oggetto
  const openReply = (ev) => {
    const fromRaw = (ev.from || "").trim();
    const mAddr = fromRaw.match(/[\w.\-+]+@[\w\-.]+\.[a-zA-Z]{2,}/);
    const to = mAddr ? mAddr[0] : fromRaw;
    const origSubj = (ev.subject || "").trim();
    const subject = /^re:/i.test(origSubj) ? origSubj : `Re: ${origSubj || "(senza oggetto)"}`;
    setReplyDialog({ emailId: ev.id, to, subject, body: "" });
  };

  const sendReply = async () => {
    if (!replyDialog) return;
    const { emailId, to, subject, body } = replyDialog;
    if (!to.trim() || !subject.trim() || !body.trim()) {
      toast.error("Destinatario, oggetto e corpo sono obbligatori");
      return;
    }
    setReplySending(true);
    try {
      // body_html: converto semplici a capo in <br/> per email HTML
      const bodyHtml = `<div>${body.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>")}</div>`;
      await api.post("/outbox/compose", {
        to,
        subject,
        body_html: bodyHtml,
        reply_to_email_id: emailId,
      });
      toast.success("Risposta inviata ✓");
      setReplyDialog(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'invio della risposta");
    } finally {
      setReplySending(false);
    }
  };

  return (
    <fieldset className="border border-slate-200 rounded-md p-3 md:p-4 space-y-2 bg-slate-50/30">
      <legend className="px-2 text-xs font-semibold text-slate-700 flex items-center gap-1.5">
        <MessageSquare className="h-3.5 w-3.5" /> Comunicazioni
        {count > 0 && <span className="text-[10px] text-slate-500">({inCount} in / {outCount} out)</span>}
      </legend>
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900"
          data-testid="quote-comms-toggle"
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          <span className="font-medium">{expanded ? "Nascondi" : "Mostra"} timeline comunicazioni</span>
        </button>
        {expanded && (
          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-slate-600"
              onClick={runBackfill}
              disabled={loading}
              data-testid="quote-comms-backfill"
              title="Ri-analizza le email Inbox esistenti per collegarle a questo preventivo"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Backfill
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-slate-600"
              onClick={load}
              disabled={loading}
              data-testid="quote-comms-refresh"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Aggiorna
            </Button>
          </div>
        )}
      </div>

      {expanded && (
        <div className="pt-1" data-testid="quote-comms-timeline">
          {loading && events.length === 0 && (
            <div className="text-xs text-slate-500 py-4 text-center">Caricamento…</div>
          )}
          {!loading && events.length === 0 && (
            <div className="text-xs text-slate-500 py-4 text-center">
              Nessuna comunicazione collegata a questo preventivo.
              <br />
              <span className="text-[11px] text-slate-400">Le email vengono collegate automaticamente se contengono il numero preventivo o provengono dal cliente/broker.</span>
            </div>
          )}
          {events.length > 0 && (
            <ol className="relative border-l-2 border-slate-200 ml-2 space-y-3 pl-4 pt-1">
              {events.map((ev) => {
                const isIn = ev.direction === "in";
                const isOpen = openBody === ev.id;
                return (
                  <li key={`${ev.kind}-${ev.id}`} className="relative" data-testid={`quote-comms-item-${ev.id}`}>
                    <span
                      className={`absolute -left-[22px] top-1 h-3 w-3 rounded-full border-2 border-white ring-2 ${
                        isIn ? "bg-blue-500 ring-blue-200" : "bg-emerald-500 ring-emerald-200"
                      }`}
                    />
                    <div className={`rounded-md border ${isIn ? "border-blue-200 bg-blue-50/40" : "border-emerald-200 bg-emerald-50/40"} p-2.5`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5 text-xs font-medium">
                          {isIn ? <Inbox className="h-3.5 w-3.5 text-blue-600" /> : <Send className="h-3.5 w-3.5 text-emerald-600" />}
                          <span className={isIn ? "text-blue-700" : "text-emerald-700"}>
                            {isIn ? "IN ENTRATA" : "IN USCITA"}
                          </span>
                          {ev.link_source && (
                            <span className="text-[10px] text-slate-500 font-normal">· {sourceLabel(ev.link_source)}</span>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-500">
                          {ev.timestamp ? new Date(ev.timestamp).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                        </span>
                      </div>
                      <div className="mt-1 text-sm font-medium text-slate-800 truncate" title={ev.subject}>{ev.subject || "(senza oggetto)"}</div>
                      <div className="text-[11px] text-slate-600 mt-0.5 flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {isIn ? <>Da: <span className="font-mono">{ev.from}</span></> : <>A: <span className="font-mono">{ev.to || "—"}</span></>}
                      </div>
                      {ev.snippet && (
                        <div className={`mt-1.5 text-[12px] text-slate-700 whitespace-pre-wrap ${isOpen ? "" : "line-clamp-2"}`}>
                          {ev.snippet}
                        </div>
                      )}
                      <div className="mt-1.5 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-[10px] text-slate-500">
                          {ev.attachments_count > 0 && (
                            <span className="inline-flex items-center gap-0.5">
                              <Paperclip className="h-3 w-3" /> {ev.attachments_count} allegat{ev.attachments_count === 1 ? "o" : "i"}
                            </span>
                          )}
                          {!isIn && ev.replied_at && (
                            <span className="inline-flex items-center gap-0.5 text-emerald-700">
                              <Mail className="h-3 w-3" /> Risposto
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {/* 🆕 Pulsante Rispondi: solo per email in ingresso (inbox) */}
                          {isIn && (
                            <button
                              type="button"
                              onClick={() => openReply(ev)}
                              className="inline-flex items-center gap-1 h-6 px-2 rounded text-[10px] font-medium border border-blue-300 bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                              data-testid={`quote-comms-reply-${ev.id}`}
                              title="Rispondi a questa email (threading tramite In-Reply-To)"
                            >
                              <Reply className="h-3 w-3" /> Rispondi
                            </button>
                          )}
                          {ev.snippet && ev.snippet.length > 100 && (
                            <button
                              type="button"
                              onClick={() => setOpenBody(isOpen ? null : ev.id)}
                              className="text-[10px] text-slate-600 hover:text-slate-900 underline"
                            >
                              {isOpen ? "Riduci" : "Espandi"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
      {/* 🆕 Dialog: Rispondi a email in ingresso */}
      <Dialog open={!!replyDialog} onOpenChange={(o) => { if (!o && !replySending) setReplyDialog(null); }}>
        <DialogContent className="max-w-2xl" data-testid="quote-comms-reply-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Reply className="h-5 w-5" /> Rispondi email
            </DialogTitle>
          </DialogHeader>
          {replyDialog && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">A</Label>
                <Input
                  data-testid="quote-comms-reply-to"
                  value={replyDialog.to}
                  onChange={(e) => setReplyDialog((prev) => ({ ...prev, to: e.target.value }))}
                  className="text-sm h-9 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Oggetto</Label>
                <Input
                  data-testid="quote-comms-reply-subject"
                  value={replyDialog.subject}
                  onChange={(e) => setReplyDialog((prev) => ({ ...prev, subject: e.target.value }))}
                  className="text-sm h-9 mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Messaggio</Label>
                <Textarea
                  data-testid="quote-comms-reply-body"
                  value={replyDialog.body}
                  onChange={(e) => setReplyDialog((prev) => ({ ...prev, body: e.target.value }))}
                  rows={8}
                  placeholder="Scrivi la tua risposta…"
                  className="text-sm mt-1"
                  autoFocus
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  💡 L&apos;email conserva il threading (In-Reply-To) con la conversazione originale.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyDialog(null)} disabled={replySending}>
              Annulla
            </Button>
            <Button
              data-testid="quote-comms-reply-send"
              onClick={sendReply}
              disabled={replySending || !replyDialog?.to?.trim() || !replyDialog?.subject?.trim() || !replyDialog?.body?.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {replySending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Invia risposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </fieldset>
  );
}

export default QuoteCommunications;
